"use client";

import { Box, Grid, GridProps, Heading, HStack, LinkBox, Stack, Text } from "@biblioteksentralen/react";
import { getPath, isNonNil, ResolvedEmployee } from "@libry-content/common";
import { TranslateSanityContent } from "@libry-content/localization";
import { Library } from "@libry-content/types";
import { ReactNode, Suspense } from "react";
import { ArrowLeft } from "react-feather";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { useSitePalette } from "../../utils/useSitePalette";
import { DefaultContainer } from "../ContentContainer";
import InternalLinkOverlay from "../InternalLinkOverlay";
import FilterButtons from "../filter/FilterButtons";
import getFilteredItems from "../filter/getFilteredItems";
import useFilter from "../filter/useFilter";
import EmployeeCard from "./EmployeeCard";

interface Props {
  employees: ResolvedEmployee[];
  nLibraries: number;
  firstLibrary: Pick<Library, "_id" | "_type" | "slug"> | null;
  filterButtons?: ReactNode;
}

export function EmployeesView(props: Props) {
  if (!props.employees) return null;

  return (
    <Suspense fallback={<EmployeesList {...props} />}>
      <EmployeesListWithFilter {...props} />
    </Suspense>
  );
}

const EmployeesList = (props: Props) => {
  const { t } = useTranslation();

  const hasActiveEmployee: boolean = props.employees.some((employee) => employee.showEmployee);

  return (
    <>
      <DefaultContainer>
        <Stack spacing="2rem">
          <Heading as="h1">{t("Ansatte")}</Heading>
          <ContactUs {...props} />
          {!hasActiveEmployee && <Text paddingBottom=".5rem">{t("Ingen ansatte å vise.")}</Text>}
          {hasActiveEmployee && props.filterButtons}
        </Stack>
      </DefaultContainer>
      <DefaultContainer withoutPaddingXBelow="sm">
        {props.employees.length > 0 && hasActiveEmployee && (
          <LayoutGrid>
            {props.employees.map((employee) => (
              <EmployeeCard employee={employee} key={employee._id} />
            ))}
          </LayoutGrid>
        )}
      </DefaultContainer>
    </>
  );
};

const EmployeesListWithFilter = (props: Props) => {
  const { ts } = useTranslation();
  const libraryFilter = useFilter("bibliotek", getLibraryName(ts));
  const filteredEmployees = getFilteredItems([libraryFilter], props.employees);

  const filterButtons = (
    <Box paddingTop="0.5rem">
      <FilterButtons filters={[libraryFilter]} filteredItems={filteredEmployees} />
    </Box>
  );

  return <EmployeesList {...props} employees={filteredEmployees.byAll} filterButtons={filterButtons} />;
};

const ContactUs = (props: Props) => {
  const palette = useSitePalette();
  const { t } = useTranslation();

  return (
    // Bruker LayoutGrid her for at boksen skal få samme bredde som EmployeeCard
    <LayoutGrid as="section">
      <HStack
        as={LinkBox}
        borderRadius="lg"
        padding="1rem"
        role="group"
        alignItems="center"
        justifyContent="space-between"
        {...palette.colors.lightaccent3.css}
      >
        <Box>
          <InternalLinkOverlay
            href={props.nLibraries > 1 || !props.firstLibrary ? "/bibliotek" : getPath(props.firstLibrary)}
          >
            <Heading as="h2" fontSize="lg" _groupHover={{ textDecoration: "underline" }}>
              {t("Lurer du på noe?")}
            </Heading>
          </InternalLinkOverlay>
          <Text>
            {props.nLibraries === 1
              ? t("Finn kontaktinformasjonen til biblioteket her.")
              : t("Finn kontaktinformasjonen til bibliotekene her.")}
          </Text>
        </Box>
        <Box
          transform="rotate(180deg)"
          paddingRight="0.5rem"
          cursor="pointer"
          _groupHover={{ transform: "rotate(180deg) translateX(-3px)" }}
          transition="transform 0.3s ease"
        >
          <ArrowLeft size="1em" />
        </Box>
      </HStack>
    </LayoutGrid>
  );
};

const LayoutGrid = (props: GridProps) => (
  <Grid gap="1rem 1rem" gridTemplateColumns={{ base: "1fr", md: "repeat(auto-fit, 26rem)" }} {...props} />
);

const getLibraryName =
  (ts: TranslateSanityContent) =>
  ({ libraries }: ResolvedEmployee) =>
    libraries?.map((library) => ts(library?.name)).filter(isNonNil) ?? [];

export default EmployeesList;
